import { useEffect, useRef } from "react";
import {
  requestFcmToken,
  registerFcmTokenWithBackend,
} from "@/lib/firebase/fcm";
import { getFirebaseMessaging, getFirebaseAuth } from "@/lib/firebase/config";
import { onMessage } from "firebase/messaging";
import { signInWithCustomToken } from "firebase/auth";

type FcmBootstrapProps = {
  adminId: string | undefined;
  firebaseToken?: string | null;
};

export function FcmBootstrap({ adminId, firebaseToken }: FcmBootstrapProps) {
  const registeredRef = useRef<string | null>(null);

  // Register/refresh FCM token on mount — request permission if not yet granted
  useEffect(() => {
    if (!adminId) return;
    if (typeof Notification === "undefined") return;
    if (registeredRef.current === adminId) return;

    let cancelled = false;
    void (async () => {
      // ponytail: never auto-prompt; NotificationPromptBanner owns the request on user click.
      if (Notification.permission !== "granted") return;

      const token = await requestFcmToken();
      if (cancelled || !token) return;
      await registerFcmTokenWithBackend(token);
      registeredRef.current = adminId;
    })();

    return () => {
      cancelled = true;
    };
  }, [adminId]);

  // Sign in to Firebase Auth for Firestore access (re-signs on token refresh)
  useEffect(() => {
    if (!firebaseToken) return;
    const auth = getFirebaseAuth();
    if (!auth) return;

    void signInWithCustomToken(auth, firebaseToken).catch((err) => {
      console.warn("Firebase Auth sign-in failed", err);
    });
  }, [firebaseToken]);

  // Foreground messages are handled by useNotifications (Firestore listener).
  // We only need to consume the onMessage stream so Firebase doesn't warn.
  useEffect(() => {
    const messaging = getFirebaseMessaging();
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, () => {
      // No-op: in-app notification UI is driven by Firestore snapshots
    });

    return () => unsubscribe();
  }, []);

  return null;
}
